import React, { useCallback, useState } from 'react';

import { LoopIcon } from '@lp/ds-next';
import { Button, Flex, Modal, Typography, theme } from 'antd';
import { useParams } from 'react-router-dom';

import useRemoteControl from './hook/useRemoteControl';
import { RemoteError } from './RemoteControlPanel';
import { useHandleCleanupSuccess } from '../../api/handleCleanupSuccess';
import PillButton from '@/components/PillButton';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

const COMMAND_ALREADY_RUN = 'deviceProductAlreadyHaveProgressingJob';

type CardCommandProps = {
  cardContent: any;
  isDirectMode: boolean;
  isConnected: boolean;
  shipmentId?: string;
  disabled?: boolean;
  setError: (value: boolean) => void;
  setErrorMessage: (value: RemoteError) => void;
  setWarning: (value: boolean) => void;
  setWarningMessage: (value: RemoteError) => void;
  setShowFinalNotice: (value: boolean) => void;
  onCleanupComplete?: () => void;
  //   getLockState: () => void;
};

export const CardCommand = ({
  cardContent,
  setShowFinalNotice,
  isDirectMode,
  isConnected,
  shipmentId,
  disabled,
  setErrorMessage,
  setError,
  setWarning,
  setWarningMessage,
  onCleanupComplete,
  //   getLockState,
}: CardCommandProps) => {
  const { token } = theme.useToken();
  const { id } = useParams<{ id: string }>();
  const addToast = useToasts((state) => state.addItem);

  // 2. Domain hooks
  const { sendRemoteAction, getRemoteActionList } = useRemoteControl(id);
  const { mutate: processCleanupSuccess } = useHandleCleanupSuccess();

  // 3. Local state
  const [commandLoader, setCommandLoader] = useState<boolean>(false);
  const [commandStatus, setCommandStatus] = useState<string>('');
  const [allowRefresh, setAllowRefresh] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isUpdatingShipment, setIsUpdatingShipment] = useState<boolean>(false);

  // 4. Callbacks
  const timeoutConfirmAction = useCallback(
    (command: any, delay: number) => {
      setTimeout(async () => {
        const listcommand = await getRemoteActionList(id);
        const lastCommand =
          listcommand.find((cmd: any) => cmd.type === command.action) ??
          listcommand[0];

        command.status = lastCommand.status;
        setCommandStatus(lastCommand.status);
        setCommandLoader(false);
        setAllowRefresh(true);
        // getLockState();

        if (
          lastCommand.type === 'COMMAND_TYPE_RECEIVED' &&
          lastCommand.status === 'SUCCEEDED'
        ) {
          setShowFinalNotice(true);
        }

        if (
          lastCommand.type === 'COMMAND_TYPE_CLEANUP' &&
          lastCommand.status === 'SUCCEEDED'
        ) {
          // Show updating shipment status loader
          setIsUpdatingShipment(true);

          const result = await processCleanupSuccess(shipmentId);

          setIsUpdatingShipment(false);

          if (result.needsUpdate && !result.success) {
            // Cleanup succeeded but contract state update failed
            command.status = 'SUCCEEDED';
            setErrorMessage({
              title: 'Contract state update failed',
              message:
                result.error ||
                'Cleanup succeeded but failed to update contract states. Please check shipment manually.',
            });
            setError(true);
          } else if (result.needsUpdate && result.success) {
            // Cleanup + contract state update both succeeded
            command.status = 'SUCCEEDED';
            // Display success message in a toast instead of overwriting status
            if (result.message) {
              addToast(
                {
                  msg: result.message,
                  type: MessageType.Success,
                },
                'root',
                { duration: 5000 }
              );
            }
            // Refresh data after a delay to let backend stabilize
            setTimeout(() => {
              onCleanupComplete?.();
            }, 1500);
          } else {
            // Cleanup succeeded, no update needed (e.g., no shipment or not DEPLOYED)
            command.status = 'SUCCEEDED';
            // Refresh data anyway
            setTimeout(() => {
              onCleanupComplete?.();
            }, 1500);
          }
        }

        if (lastCommand.status === 'FAILED') {
          setErrorMessage({
            title: 'Action error',
            message: `An error occured while "${command.action}" running`,
          });

          setError(true);
        }
      }, delay);
    },
    [
      getRemoteActionList,
      id,
      setError,
      setErrorMessage,
      setShowFinalNotice,
      processCleanupSuccess,
      shipmentId,
      onCleanupComplete,
      addToast,
    ]
  );

  // Run command action
  const handleClickAction = useCallback(
    async (command: any) => {
      if (!isDirectMode || !isConnected) {
        setWarning(true);
        setWarningMessage({
          title: 'Connection required',
          message:
            'The box must be connected and in direct mode to execute this action.',
        });

        return;
      }

      setWarning(false);
      setError(false);
      setCommandStatus('');
      setCommandLoader(true);

      try {
        await sendRemoteAction(id, shipmentId, command.action);
      } catch (error: any) {
        if (error.response.data.message.includes(COMMAND_ALREADY_RUN)) {
          setErrorMessage({
            title: 'Action error',
            message: `"${command.action}" is already running`,
          });
          setCommandLoader(false);
        }

        setError(true);

        return;
      }

      timeoutConfirmAction(command, 10000);
    },
    [
      isDirectMode,
      isConnected,
      sendRemoteAction,
      id,
      shipmentId,
      timeoutConfirmAction,
      setError,
      setErrorMessage,
      setWarning,
      setWarningMessage,
    ]
  );

  // Refresh status command
  const handleClickReload = useCallback(
    async (command) => {
      setCommandLoader(true);

      timeoutConfirmAction(command, 1500);
    },
    [timeoutConfirmAction]
  );

  const isHardDisabled = disabled || commandStatus === 'IN_PROGRESS';
  const isNotReady = !isDirectMode || !isConnected;

  // Handle button click - open modal if confirmation required, else execute directly
  const handleButtonClick = useCallback(() => {
    if (isNotReady) {
      setWarning(true);
      setWarningMessage({
        title: 'Connection required',
        message:
          'The box must be connected and in direct mode to execute this action.',
      });

      return;
    }

    if (cardContent.requiresConfirmation) {
      setIsConfirmModalOpen(true);
    } else {
      handleClickAction(cardContent);
    }
  }, [
    isNotReady,
    cardContent,
    handleClickAction,
    setWarning,
    setWarningMessage,
  ]);

  // Handle confirmation - execute action after modal confirmation
  const handleConfirmAction = useCallback(() => {
    setIsConfirmModalOpen(false);
    handleClickAction(cardContent);
  }, [cardContent, handleClickAction]);

  return (
    <Flex
      vertical
      style={{
        width: '25.3125rem',
        padding: '.875rem',
        borderRadius: '.75rem',
        border: '.0625rem solid',
        borderColor: token.colorBorderSecondary,
        opacity: disabled ? 0.3 : 1,
        pointerEvents: disabled ? 'none' : 'initial',
      }}
    >
      <Flex vertical align="center">
        <Typography.Text strong style={{ marginBottom: '.25rem' }}>
          {cardContent.title}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ marginBottom: '1.5rem' }}>
          {cardContent.subtitle}
        </Typography.Text>
      </Flex>
      <Flex
        vertical
        align="center"
        justify="center"
        style={{ minHeight: '6rem', marginBottom: '.75rem' }}
      >
        {commandLoader || isUpdatingShipment ? (
          isUpdatingShipment ? (
            <Flex vertical style={{ marginBottom: '0.8rem' }}>
              {cardContent.loaderShipment}
            </Flex>
          ) : (
            cardContent.loader
          )
        ) : (
          <PillButton
            style={{
              opacity: isHardDisabled || isNotReady ? 0.5 : 1,
              pointerEvents: isHardDisabled ? 'none' : undefined,
            }}
            onClick={handleButtonClick}
            data-testid="command-button"
          >
            {cardContent.name}
          </PillButton>
        )}
      </Flex>
      <Flex
        style={{
          padding: '.875rem 1.5rem',
          borderRadius: '.75rem',
          backgroundColor: token.colorBgLayout,
        }}
        justify="space-between"
      >
        <Flex gap="1rem">
          <Flex
            onClick={() => {
              if (allowRefresh) {
                handleClickReload(cardContent);
              }
            }}
            data-testid="refresh-command-status"
          >
            <LoopIcon
              style={{
                width: '1.25rem',
                height: '1.25rem',
                cursor: allowRefresh ? 'pointer' : 'initial',
                color: allowRefresh
                  ? token.colorPrimary
                  : token.colorTextDisabled,
              }}
            />
          </Flex>
          <Typography.Text strong> {cardContent.statusKey} :</Typography.Text>
        </Flex>
        <Typography.Text data-testid="remote-action-status" strong>
          {cardContent.status}
        </Typography.Text>
      </Flex>

      {/* Confirmation Modal */}
      {cardContent.requiresConfirmation && (
        <Modal
          open={isConfirmModalOpen}
          onCancel={() => setIsConfirmModalOpen(false)}
          footer={null}
        >
          <Flex vertical gap="2rem" style={{ padding: '1.5rem' }}>
            <Typography.Text strong>Confirm action</Typography.Text>
            <Typography.Text>
              {cardContent.confirmationMessage ||
                'Are you sure you want to execute this action?'}
            </Typography.Text>
            <Flex gap="1rem" justify="flex-end">
              <Button
                onClick={() => setIsConfirmModalOpen(false)}
                data-testid="cancel-confirmation-button"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmAction}
                data-testid="confirm-action-button"
              >
                Confirm
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </Flex>
  );
};
